import { observable, observe, runInAction } from "mobx"

import { formatTemplate } from "@/lib/formatTemplate.ts"
import { getLogger } from "@/lib/logging.ts"
import { Model } from "@/lib/Model.ts"
import { compareStringAsInteger, recordToMap } from "@/lib/std.ts"

import type { ListModel } from "@/components/Chat/LoadMoreButton.tsx"
import type { BatchInfo, LLMLogEntry, MessagesSorting } from "@/lib/client/generated/TypeDefinitions.ts"
import type { Persistence } from "@/lib/persistence.ts"
import type { Queries } from "@/lib/queries.ts"
import type { IObservableValue } from "mobx"


const log = getLogger("LLMLogChatModel")

export interface LLMLogChatModel extends ListModel {
  logs: IObservableValue<LLMLogModel[]>
  hasNext: IObservableValue<boolean>
  hasPrev: IObservableValue<boolean>
  isLoadingNext: IObservableValue<boolean>
  isLoadingPrev: IObservableValue<boolean>
  isLoadingInitially: IObservableValue<boolean>
  isReady: IObservableValue<boolean>

  loadNext(): void

  loadPrev(): void

  terminate(): void
}

export type LLMLogModel = LLMLogEntry & {
  collapsed: IObservableValue<boolean>
  formattedMessage: string
  shortFormattedMessage: string

  toggleCollapsed(): void
}

export function createLLMLogChatModel(
  taskId: string,
  queries: Queries,
  persistence: Persistence,
  pageId: string | null,
  batchSize: number = 20,
): LLMLogChatModelImpl {
  return new LLMLogChatModelImpl(taskId, queries, persistence, pageId, batchSize)
}

const COLLAPSE_SYSTEM_AND_CONTEXT_MESSAGES_KEY = "collapse-system-and-context-messages"
const DISABLE_AUTOSCROLL_KEY = "disable-autoscroll"

class LLMLogChatModelImpl extends Model implements LLMLogChatModel {
  private readonly logComparator: (a: LLMLogEntry, b: LLMLogEntry) => number = (a, b) => {
    return compareStringAsInteger(a.longId, b.longId)
  }

  private readonly rawLogs = new Map<string, LLMLogEntry>()
  private readonly updateQueue: LoadType[] = []
  private isProcessing: boolean = false

  logs = observable.box<LLMLogModel[]>([])
  hasNext = observable.box(false)
  hasPrev = observable.box(true)
  isLoadingNext = observable.box(false)
  isLoadingPrev = observable.box(false)
  isLoadingInitially = observable.box(true)
  isReady = observable.box(false)
  collapseSystemAndContextMessages = observable.box(true)
  disableAutoscroll = observable.box(false)

  constructor(private readonly taskId: string, private readonly queries: Queries, private readonly persistence: Persistence, private readonly pageId: string | null, private readonly batchSize: number = 20) {
    super("LLMLogChatModelImpl")

    runInAction(() => {
      this.collapseSystemAndContextMessages.set(persistence.getItem(COLLAPSE_SYSTEM_AND_CONTEXT_MESSAGES_KEY) === null || persistence.getItem(COLLAPSE_SYSTEM_AND_CONTEXT_MESSAGES_KEY) === "true")
      this.disableAutoscroll.set(persistence.getItem(DISABLE_AUTOSCROLL_KEY) === "true")
    })

    this.lifetime.add(observe(this.collapseSystemAndContextMessages, (value) => {
      this.persistence.setItem(COLLAPSE_SYSTEM_AND_CONTEXT_MESSAGES_KEY, value.newValue.toString())
      this.updateLogsList()
    }), "LLMLogChatModelImpl:collapseSystemAndContextMessages")

    this.lifetime.add(observe(this.disableAutoscroll, (value) => {
      this.persistence.setItem(DISABLE_AUTOSCROLL_KEY, value.newValue.toString())
    }), "LLMLogChatModelImpl:disableAutoscroll")

    void this.startQueueProcessing()
    void this.listenForUpdates()
  }

  readonly loadNext = () => {
    this.scheduleUpdate("load-next")
  }

  readonly loadPrev = () => {
    this.scheduleUpdate("load-prev")
  }

  private listenForUpdates() {
    const updatesChannel = this.queries.llmLogUpdates(this.taskId, this.pageId)
    this.scheduleUpdate("update-recent")

    this.lifetime.add(updatesChannel.subscribe(() => {
      if (!this.hasNext.get()) {
        this.scheduleUpdate("update-recent")
      }
    }), "LLMLogChatModelImpl:updatesChannel")
  }

  terminate() {
    super.terminate()
    this.updateQueue.length = 0
  }

  private async startQueueProcessing() {
    if (this.isProcessing) {
      return
    }

    this.isProcessing = true

    while (this.updateQueue.length > 0) {
      try {
        const request = this.updateQueue.shift()!
        log.debug("Process event", request)
        if (request === "load-next" && !this.isLoadingNext.get() && this.hasNext.get()) {
          await this.load("load-next")
        } else if (request === "load-prev" && !this.isLoadingPrev.get() && this.hasPrev.get()) {
          await this.load("load-prev")
        } else {
          await this.load(request)
        }
      } catch (ex) {
        log.error("Error during queue processing:", ex)
      }
    }

    this.isProcessing = false
  }

  private scheduleUpdate(update: LoadType) {
    for (const existingUpdate of this.updateQueue) {
      if (existingUpdate === update) {
        return
      }
    }

    this.updateQueue.push(update)

    if (!this.isProcessing) {
      void this.startQueueProcessing()
    }
  }

  private getLatestLogId(): string | null {
    if (this.rawLogs.size == 0) {
      return null
    }

    return [...this.rawLogs.values()].reduce((max, current) =>
      compareStringAsInteger(current.longId, max.longId) > 0 ? current : max
    ).id
  }

  private getOldestLogId(): string | null {
    if (this.rawLogs.size == 0) {
      return null
    }

    return [...this.rawLogs.values()].reduce((min, current) =>
      compareStringAsInteger(current.longId, min.longId) < 0 ? current : min
    ).id
  }

  private consumeBatch(batch: LLMLogEntry[]) {
    for (const item of batch) {
      this.rawLogs.set(item.id, item)
    }

    this.updateLogsList()
  }

  private resetState() {
    runInAction(() => {
      this.rawLogs.clear()
      this.updateQueue.length = 0
      this.hasNext.set(false)
      this.hasPrev.set(true)
    })
  }

  private async getBatch(offset: string | null, sorting: MessagesSorting, type: LoadType): Promise<LLMLogEntry[]> {
    const batchInfo: BatchInfo = { offset, batchSize: this.batchSize }
    const batch = (await this.queries.llmLog(this.taskId, this.pageId, batchInfo, sorting)).data
    if (batch.length < this.batchSize) {
      if (sorting === "FromNewestToOldest") {
        runInAction(() => this.hasPrev.set(false))
      } else if (sorting === "FromOldestToNewest") {
        runInAction(() => this.hasNext.set(false))
      }
    } else if (type === "update-recent") {
      let newMessagesCount = 0
      for (const message of batch) {
        if (!this.rawLogs.has(message.id)) {
          newMessagesCount++
        }
      }
      if (newMessagesCount >= this.batchSize) {
        // Too many updates, try to reload
        this.resetState()
      }
    }
    return batch
  }

  private async load(type: LoadType) {
    let sorting: MessagesSorting | null = null
    let offset: string | null = null

    runInAction(() => {
      this.isLoadingNext.set(type === "load-next")
      this.isLoadingPrev.set(type === "load-prev")
    })

    try {
      if (type === "load-prev") {
        sorting = "FromNewestToOldest"
        offset = this.getOldestLogId()
      } else if (type === "load-next") {
        sorting = "FromOldestToNewest"
        offset = this.getLatestLogId()
      } else if (type === "update-recent") {
        sorting = "FromNewestToOldest"
        offset = null
      } else {
        return
      }

      this.consumeBatch(await this.getBatch(offset, sorting, type))
    } finally {
      runInAction(() => {
        this.isLoadingPrev.set(false)
        this.isLoadingNext.set(false)
        this.isLoadingInitially.set(false)
        this.isReady.set(true)
      })
    }
  }

  private updateLogsList() {
    const logs: LLMLogModel[] = []
    for (const entry of this.rawLogs.values()) {
      const formattedMessage = formatTemplate(entry.message, recordToMap(entry.params))

      const lines = formattedMessage.split("\n")
      const shortFormattedMessage = lines.length > 3 ? lines.slice(0, 3).join("\n") : formattedMessage

      const entryModel: LLMLogModel = {
        ...entry,

        collapsed: observable.box(
          this.collapseSystemAndContextMessages.get() && (entry.role == "System" || entry.isContext == true)
        ),


        formattedMessage: formattedMessage,
        shortFormattedMessage: shortFormattedMessage,

        toggleCollapsed: () => {
          runInAction(() => entryModel.collapsed.set(!entryModel.collapsed.get()))
        }
      }
      logs.push(entryModel)
    }

    logs.sort(this.logComparator)
    log.debug(`Total logs count: ${logs.length}`)

    runInAction(() => this.logs.set(logs))
  }
}

type LoadType = "update-recent" | "load-next" | "load-prev"